import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HeroLine, FadeUp } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1706374503312-7a4a4c030d2d?q=80&w=1600&auto=format&fit=crop";

const Hero = () => {
    const sectionRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 18 });
    const sy = useSpring(my, { stiffness: 55, damping: 18 });
    const rotateY = useTransform(sx, [-0.5, 0.5], [-9, 9]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [7, -7]);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    const onMouseMove = (e) => {
        const r = sectionRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="top"
            ref={sectionRef}
            onMouseMove={onMouseMove}
            data-testid="hero-section"
            className="relative min-h-screen bg-[#0A0A0A] noise-overlay overflow-hidden flex items-center"
        >
            <div className="absolute inset-0 hero-grid-lines" />
            <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#0047FF]/25 blur-[140px]" />
            <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#FF6B00]/15 blur-[120px]" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-36 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                <motion.div style={{ y: textY }} className="lg:col-span-7">
                    <FadeUp delay={0.15} y={20}>
                        <p data-testid="hero-eyebrow" className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF8A3D] mb-8">
                            Bouw de meerjarenbegroting en analyseer uw investeringen met de hulp van AI
                        </p>
                    </FadeUp>

                    <h1 className="font-heading text-white font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-[5.4rem]">
                        <HeroLine delay={0.3}>Elke investering.</HeroLine>
                        <HeroLine delay={0.45}>Elk scenario.</HeroLine>
                        <HeroLine delay={0.6} className="text-[#FF6B00]">In minuten.</HeroLine>
                    </h1>

                    <FadeUp delay={0.85} className="mt-9 max-w-xl">
                        <p data-testid="hero-subtext" className="text-white/60 text-base sm:text-lg leading-relaxed">
                            MeerjarenplanOnline.nl leest je jaarverslag, bouwt ongelimiteerde scenario's en
                            rekent de kansen door met Monte Carlo. Financieel inzicht dat
                            overtuigt — in minuten, niet in weken.
                        </p>
                    </FadeUp>

                    <FadeUp delay={1.0} className="mt-10 flex flex-col items-start gap-4">
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToSection("#hoe-het-werkt")}
                            className="inline-flex items-center gap-2 rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold px-8 py-4 text-sm transition-[background-color,transform] duration-300 hover:scale-[1.04]"
                        >
                            Zie hoe het werkt
                            <ArrowDown className="w-4 h-4" />
                        </button>
                        <a
                            data-testid="hero-cta-primary"
                            href="https://app.meerjarenplanonline.nl/login"
                            className="inline-flex items-center gap-2 rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white font-bold px-8 py-4 text-sm transition-[background-color,transform] duration-300 hover:scale-[1.04]"
                        >
                            Start gratis proefperiode
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                        <a
                            data-testid="hero-cta-app"
                            href="https://app.meerjarenplanonline.nl/login"
                            className="inline-flex items-center gap-2 rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold px-8 py-4 text-sm transition-[background-color,transform] duration-300 hover:scale-[1.04]"
                        >
                            Naar de Scenariobouwer
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </FadeUp>
                </motion.div>

                <div className="lg:col-span-5 relative" style={{ perspective: 1100 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        style={{ rotateX, rotateY, y: imgY, transformStyle: "preserve-3d" }}
                        className="relative"
                    >
                        <div className="relative rounded-2xl overflow-hidden border border-white/10 blue-glow">
                            <img
                                data-testid="hero-visual"
                                src={HERO_IMG}
                                alt="Gouden medailles op een rij — het winnende scenario met MeerjarenplanOnline.nl"
                                className="w-full h-[420px] lg:h-[520px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50">Kansberekening</p>
                                    <p className="font-heading text-white text-3xl font-black tracking-tight">P10 – P90</p>
                                </div>
                                <div className="rounded-2xl bg-[#FF6B00] px-5 py-4 max-w-[230px]">
                                    <p className="text-white text-xs font-bold leading-snug">…inclusief analyse met Monte Carlo simulatie technieken</p>
                                </div>
                            </div>
                        </div>

                        <div className="absolute -top-5 -right-4 animate-float-slow rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl px-5 py-4" style={{ transform: "translateZ(60px)" }}>
                            <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50">Scenario's</p>
                            <p className="font-heading text-white text-2xl font-black">Ongelimiteerd</p>
                        </div>
                        <div className="absolute top-[38%] -left-5 animate-float-slow rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl px-5 py-4" style={{ transform: "translateZ(40px)", animationDelay: "1.4s" }}>
                            <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50">Jaarverslag import</p>
                            <p className="font-heading text-white text-2xl font-black">&lt; 1 minuut</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <FadeUp delay={1.5} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:block">
                <div className="flex flex-col items-center gap-2 text-white/40">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Scroll</span>
                    <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                        <ArrowDown className="w-4 h-4" />
                    </motion.div>
                </div>
            </FadeUp>
        </section>
    );
};

export default Hero;
